import { useState } from "react";
import { useLocalSearchParams, useRouter } from "expo-router";
import * as Location from "expo-location";
import { StyleSheet, View } from "react-native";

import { UpgradePrompt } from "~/components/composed/UpgradePrompt";
import {
  AppText,
  Badge,
  Button,
  ButtonText,
  Card,
  EmptyState,
  ListRow,
  ProtectedScreen,
  SearchableSelect,
  SectionHeader,
  SegmentedControl,
  Stack,
  TextField,
} from "~/components/ui";
import { AddPlantModal } from "~/features/plants/AddPlantModal";
import { CollectionEmailShare } from "~/features/collections/CollectionEmailShare";
import {
  DEFAULT_MAP_CENTER,
  DEFAULT_MAP_ZOOM,
  GEO_MAP_STYLE_OPTIONS,
} from "~/features/maps/map-styles";
import { pickImage, uploadImage } from "~/features/media/storage";
import { useAuth } from "~/hooks/useAuth";
import { usePlan } from "~/hooks/usePlan";
import { useCollection, useCollectionActions } from "~/state/collections";
import { usePlants } from "~/state/plants";
import type { Collection } from "~/state/sync";
import { spacing } from "~/styles/theme";
import type { CollectionMapKind, GeoMapStyle } from "~/api/types";

const mapKindOptions: { label: string; value: CollectionMapKind }[] = [
  { label: "No map", value: "none" },
  { label: "Real world", value: "geo" },
  { label: "Uploaded plan", value: "custom" },
];

const visibilityOptions = [
  { value: "private", label: "Private" },
  { value: "unlisted", label: "Unlisted link" },
  { value: "public", label: "Public" },
];

export default function CollectionDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const collection = useCollection(id);

  if (!collection) {
    return (
      <ProtectedScreen narrow title="Collection">
        <EmptyState
          title="Collection not found"
          description="It may have been deleted on another device."
        />
      </ProtectedScreen>
    );
  }

  return <CollectionDetail collection={collection} />;
}

function CollectionDetail({ collection }: { collection: Collection }) {
  const router = useRouter();
  const { user } = useAuth();
  const plan = usePlan();
  const plants = usePlants(collection.id);
  const actions = useCollectionActions();
  const [addOpen, setAddOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);

  async function setMapKind(kind: CollectionMapKind) {
    setMapError(null);
    if (kind === "custom" && !plan.canUseCustomMaps) {
      setMapError("Uploading your own map is a Florafolio Pro feature.");
      return;
    }

    if (kind === "geo") {
      // Centre on the user so the first pin drop is somewhere useful, but a
      // denied permission shouldn't block enabling the map.
      let center = DEFAULT_MAP_CENTER;
      try {
        const permission = await Location.requestForegroundPermissionsAsync();
        if (permission.granted) {
          const position = await Location.getCurrentPositionAsync({});
          center = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
        }
      } catch {
        center = DEFAULT_MAP_CENTER;
      }
      actions.configureMap(collection.id, {
        mapKind: "geo",
        centerLat: collection.map_center_lat ?? center.lat,
        centerLng: collection.map_center_lng ?? center.lng,
        zoom: collection.map_zoom ?? DEFAULT_MAP_ZOOM,
      });
      return;
    }

    if (kind === "custom") {
      if (collection.custom_map_path) {
        actions.configureMap(collection.id, { mapKind: "custom" });
        return;
      }
      await uploadCustomMap();
      return;
    }

    actions.configureMap(collection.id, { mapKind: "none" });
  }

  async function uploadCustomMap() {
    if (!user) return;
    setMapError(null);
    setUploading(true);
    try {
      const image = await pickImage();
      if (!image) return;
      const path = await uploadImage("collection-maps", user.id, image);
      actions.configureMap(collection.id, {
        mapKind: "custom",
        customMapPath: path,
        customMapWidth: image.width,
        customMapHeight: image.height,
      });
    } catch (error) {
      setMapError(
        error instanceof Error
          ? error.message
          : "Unable to upload that map. Uploads need a connection.",
      );
    } finally {
      setUploading(false);
    }
  }

  return (
    <ProtectedScreen
      narrow
      title={collection.name}
      subtitle={collection.description ?? undefined}
    >
      <Card gap="md">
        <SectionHeader
          title={`${plants.length} ${plants.length === 1 ? "plant" : "plants"}`}
          description="Tap a plant to edit its details, photos, and position on the map."
        />
        {plants.length === 0 ? (
          <EmptyState
            title="Nothing catalogued yet"
            description="Search the botanical index to add your first plant."
          />
        ) : (
          <Stack gap="sm">
            {plants.map((plant) => (
              <ListRow
                key={plant.id}
                title={plant.display_name}
                description={plant.notes ?? undefined}
                onPress={() => router.push(`/plants/${plant.id}` as never)}
                meta={
                  <>
                    <Badge tone="neutral">{plant.status}</Badge>
                    <Badge tone="neutral">
                      {plant.placement.replace(/_/g, " ")}
                    </Badge>
                    {plant.map_lat !== null || plant.map_x !== null ? (
                      <Badge tone="success">On the map</Badge>
                    ) : null}
                  </>
                }
              />
            ))}
          </Stack>
        )}
        <Button
          align="start"
          iconName="plus"
          disabled={!plan.plants.allowed}
          onPress={() => setAddOpen(true)}
        >
          <ButtonText>Add a plant</ButtonText>
        </Button>
        {!plan.plants.allowed ? (
          <AppText size="xs">{plan.plants.message}</AppText>
        ) : null}
      </Card>

      <Card gap="md">
        <SectionHeader
          title="Map"
          description="Pin each plant to a real-world location, or to your own hand-drawn garden plan."
        />
        <SegmentedControl
          accessibilityLabel="Map type"
          options={mapKindOptions}
          value={collection.map_kind}
          onChange={(value) => void setMapKind(value)}
        />

        {collection.map_kind === "geo" ? (
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Base map
            </AppText>
            <SearchableSelect
              title="Choose a base map"
              accessibilityLabel="Base map"
              value={collection.map_style}
              options={GEO_MAP_STYLE_OPTIONS}
              placeholder="Streets"
              onValueChange={(value) =>
                actions.configureMap(collection.id, {
                  mapKind: "geo",
                  mapStyle: value as GeoMapStyle,
                })
              }
            />
          </View>
        ) : null}

        {collection.map_kind === "custom" ? (
          <Button
            variant="outline"
            align="start"
            iconName="image"
            disabled={uploading}
            onPress={() => void uploadCustomMap()}
          >
            <ButtonText>
              {uploading ? "Uploading..." : "Replace map image"}
            </ButtonText>
          </Button>
        ) : null}

        {mapError ? (
          <AppText size="xs" tone="danger">
            {mapError}
          </AppText>
        ) : null}

        {collection.map_kind !== "none" ? (
          <Button
            align="start"
            iconName="map"
            onPress={() =>
              router.push(`/collections/${collection.id}/map` as never)
            }
          >
            <ButtonText>Open map view</ButtonText>
          </Button>
        ) : null}
      </Card>

      {!plan.canUseCustomMaps ? (
        <UpgradePrompt
          title="Upload your own garden plan"
          reason="Florafolio Pro lets you pin plants onto a site plan, nursery layout, or hand-drawn map — plus unlimited collections and plants."
        />
      ) : null}

      <Card gap="md">
        <SectionHeader
          title="Collection settings"
          description="Rename it, describe it, or share it."
        />
        <View style={stylesheet.field}>
          <AppText size="xs" weight="semibold">
            Name
          </AppText>
          <TextField
            accessibilityLabel="Collection name"
            value={collection.name}
            placeholder="Front garden"
            onChangeText={(value) =>
              actions.update(collection.id, { name: value })
            }
          />
        </View>
        <View style={stylesheet.field}>
          <AppText size="xs" weight="semibold">
            Description
          </AppText>
          <TextField
            accessibilityLabel="Collection description"
            value={collection.description ?? ""}
            placeholder="Everything along the north fence"
            multiline
            style={stylesheet.textarea}
            onChangeText={(value) =>
              actions.update(collection.id, { description: value })
            }
          />
        </View>
        <View style={stylesheet.field}>
          <AppText size="xs" weight="semibold">
            Visibility
          </AppText>
          <SearchableSelect
            title="Choose visibility"
            accessibilityLabel="Visibility"
            value={collection.visibility}
            options={visibilityOptions}
            placeholder="Private"
            onValueChange={(value) =>
              actions.update(collection.id, {
                visibility: value as typeof collection.visibility,
              })
            }
          />
        </View>
        <CollectionEmailShare collectionId={collection.id} />
        <Button
          variant="danger"
          align="start"
          iconName="trash-2"
          onPress={() => {
            actions.remove(collection.id);
            router.replace("/collections" as never);
          }}
        >
          <ButtonText>Delete collection</ButtonText>
        </Button>
      </Card>

      <AddPlantModal
        collectionId={collection.id}
        visible={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={(plantId) => router.push(`/plants/${plantId}` as never)}
      />
    </ProtectedScreen>
  );
}

const stylesheet = StyleSheet.create({
  field: { gap: spacing.xs },
  textarea: { minHeight: 86, textAlignVertical: "top" },
});
